import { createSlice } from "@reduxjs/toolkit";

// Light, dark or "auto" (follow the computer's or phone's setting, the
// default until someone chooses), and the text size, both kept across
// reloads. Storage can be blocked: then the choice lasts until reload.
const MODE_KEY = "safrati.cms.mode";
const TEXT_KEY = "safrati.cms.textSize";
const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* blocked storage */
  }
};
export const systemMode = () => {
  try {
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
};
const savedPreference = () => {
  const mode = read(MODE_KEY);
  return mode === "dark" || mode === "light" ? mode : "auto";
};
const resolveMode = (preference) => (preference === "auto" ? systemMode() : preference);
const savedTextSize = () => (read(TEXT_KEY) === "larger" ? "larger" : "standard");

const initialState = {
  activeDir: "ltr",
  modePreference: savedPreference(), // light, dark or auto
  activeMode: resolveMode(savedPreference()), // light or dark, as shown
  textSize: savedTextSize(), // standard or larger
  activeTheme: "SAFRATI_THEME", // SAFRATI_THEME, BLUE_THEME, GREEN_THEME, RED_THEME, BLACK_THEME, PURPLE_THEME, INDIGO_THEME
  SidebarWidth: 264,
  MiniSidebarWidth: 87,
  TopbarHeight: 70,
  isLayout: "full", // This can be full or boxed
  isCollapse: false, // to make sidebar Mini by default
  isSidebarHover: false,
  isMobileSidebar: false,
  isHorizontal: false,
  isLanguage: "en",
  isCardShadow: true,
  borderRadius: 14,
  loading: false,
  Modal: {
    show: false,
    title: "",
    message: "",
    action: [],
    component: "",
  },
};

export const CustomizerSlice = createSlice({
  name: "customizer",
  initialState,
  reducers: {
    setTheme: (state, action) => {
      state.activeTheme = action.payload;
    },
    // The top bar's sun / moon: an explicit light or dark.
    setDarkMode: (state, action) => {
      state.modePreference = action.payload;
      state.activeMode = action.payload;
      write(MODE_KEY, action.payload);
    },
    // My settings: light, dark or auto.
    setModePreference: (state, action) => {
      const preference = ["light", "dark"].includes(action.payload) ? action.payload : "auto";
      state.modePreference = preference;
      state.activeMode = resolveMode(preference);
      write(MODE_KEY, preference === "auto" ? null : preference);
    },
    // The computer or phone switched between light and dark.
    systemModeChanged: (state, action) => {
      if (state.modePreference === "auto") state.activeMode = action.payload === "dark" ? "dark" : "light";
    },
    setTextSize: (state, action) => {
      state.textSize = action.payload === "larger" ? "larger" : "standard";
      write(TEXT_KEY, state.textSize === "larger" ? "larger" : null);
    },
    setDir: (state, action) => {
      state.activeDir = action.payload;
    },
    setLanguage: (state, action) => {
      state.isLanguage = action.payload;
    },
    setCardShadow: (state, action) => {
      state.isCardShadow = action.payload;
    },
    toggleSidebar: (state) => {
      state.isCollapse = !state.isCollapse;
    },
    hoverSidebar: (state, action) => {
      state.isSidebarHover = action.payload;
    },
    toggleMobileSidebar: (state) => {
      state.isMobileSidebar = !state.isMobileSidebar;
    },
    closeMobileSidebar: (state) => {
      state.isMobileSidebar = false;
    },
    toggleLayout: (state, action) => {
      state.isLayout = action.payload;
    },
    toggleHorizontal: (state, action) => {
      state.isHorizontal = action.payload;
    },
    setBorderRadius: (state, action) => {
      state.borderRadius = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setShowModal: (state, action) => {
      state.Modal = action.payload;
    },
  },
});

export const {
  setTheme,
  setDarkMode,
  setModePreference,
  systemModeChanged,
  setTextSize,
  setDir,
  toggleSidebar,
  hoverSidebar,
  toggleMobileSidebar,
  closeMobileSidebar,
  toggleLayout,
  setBorderRadius,
  toggleHorizontal,
  setLanguage,
  setCardShadow,
  setLoading,
  setShowModal,
} = CustomizerSlice.actions;

export default CustomizerSlice.reducer;
